/**
 * CEO Command Center v11 - Database Service
 *
 * CR-SQLite wrapper providing:
 * - SQLite database operations
 * - CRDT-enabled tables for sync
 * - Device identity management
 * - Space management
 * - Change tracking for sync
 *
 * @module services/sync/database
 * @version 11.0.0
 */

import initWasm, { DB } from '@vlcn.io/crsqlite-wasm';
import type {
  DeviceIdentity,
  Space,
  SpaceMember,
  SyncPeer,
  CRSQLChange,
  DatabaseService as IDatabaseService,
} from '@/types/sync';

// Import schema as raw string
import SCHEMA_SQL from './schema.sql?raw';

const CURRENT_SCHEMA_VERSION = 1;
const DB_NAME = 'ceocc-v11.db';

// Tables that should be CRDT-enabled
const CRDT_TABLES = [
  'tasks',
  'deals',
  'clients',
  'owners',
  'cashflow',
  'investors',
  'mau_data',
  'customer_mau',
  'initiatives',
  'board_members',
  'deck_sections',
  'meeting_history',
  'market_news',
  'knowledge_base',
];

/**
 * Database Service Implementation
 */
class DatabaseServiceImpl implements IDatabaseService {
  private db: DB | null = null;
  private _isInitialized = false;
  private _isUnlocked = false;
  private deviceKey: CryptoKey | null = null;

  /**
   * Initialize the database
   */
  async init(): Promise<void> {
    if (this._isInitialized) {
      console.warn('Database already initialized');
      return;
    }

    try {
      console.log('[DB] Starting initialization...');

      // Check for SharedArrayBuffer support (required for CR-SQLite)
      if (typeof SharedArrayBuffer === 'undefined') {
        throw new Error(
          'SharedArrayBuffer is not available. ' +
          'Ensure Cross-Origin-Opener-Policy and Cross-Origin-Embedder-Policy headers are set.'
        );
      }
      console.log('[DB] SharedArrayBuffer available');
      console.log('[DB] crossOriginIsolated:', self.crossOriginIsolated);

      // Initialize CR-SQLite WASM
      console.log('[DB] Loading CR-SQLite WASM...');
      const sqlite = await initWasm();
      console.log('[DB] WASM loaded successfully');

      // Open database (stored in IndexedDB via OPFS)
      console.log('[DB] Opening database:', DB_NAME);
      this.db = await sqlite.open(DB_NAME);
      console.log('[DB] Database opened');

      // Run migrations
      console.log('[DB] Running migrations...');
      await this.migrate();
      console.log('[DB] Migrations complete');

      // Verify persistence is working
      await this.verifyPersistence();

      this._isInitialized = true;
      console.log('[DB] Database initialized successfully');
    } catch (error) {
      console.error('[DB] Failed to initialize database:', error);
      throw error;
    }
  }

  /**
   * Verify that database persistence is working correctly
   */
  private async verifyPersistence(): Promise<void> {
    const PERSISTENCE_KEY = 'persistence_verified';
    const PERSISTENCE_TABLE = 'persistence_check';

    try {
      // Create persistence check table if not exists
      await this.db!.exec(`
        CREATE TABLE IF NOT EXISTS ${PERSISTENCE_TABLE} (
          key TEXT PRIMARY KEY,
          value TEXT NOT NULL,
          created_at INTEGER NOT NULL
        )
      `);

      // Check if we have a persistence marker
      const result = await this.db!.execO<{ value: string; created_at: number }>(
        `SELECT value, created_at FROM ${PERSISTENCE_TABLE} WHERE key = ?`,
        [PERSISTENCE_KEY]
      );

      if (result.length > 0) {
        const marker = result[0];
        const age = Date.now() - marker.created_at;
        console.log('[DB] ✅ Persistence verified! Marker age:', Math.round(age / 1000), 'seconds');
        console.log('[DB] Previous session:', marker.value);
      } else {
        // First time - create persistence marker
        const sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        await this.db!.exec(
          `INSERT INTO ${PERSISTENCE_TABLE} (key, value, created_at) VALUES (?, ?, ?)`,
          [PERSISTENCE_KEY, sessionId, Date.now()]
        );
        console.log('[DB] ⚠️ First session or persistence not working. Created marker:', sessionId);
        console.log('[DB] If you see this message on every page load, persistence is NOT working!');
      }

      // Log existing data counts
      const counts = await this.getDataCounts();
      console.log('[DB] Current data counts:', counts);
    } catch (error) {
      console.error('[DB] Persistence verification failed:', error);
    }
  }

  /**
   * Get counts of data in each table
   */
  private async getDataCounts(): Promise<Record<string, number>> {
    const counts: Record<string, number> = {};
    const tables = ['tasks', 'deals', 'clients', 'owners', 'investors'];

    for (const table of tables) {
      try {
        const result = await this.db!.execA<[number]>(`SELECT COUNT(*) FROM ${table}`);
        counts[table] = result[0]?.[0] ?? 0;
      } catch {
        counts[table] = -1; // Table doesn't exist
      }
    }

    return counts;
  }

  /**
   * Close the database connection
   */
  async close(): Promise<void> {
    if (!this.db) return;

    try {
      // Finalize CR-SQLite
      await this.db.exec('SELECT crsql_finalize()');
      await this.db.close();
      this.db = null;
      this._isInitialized = false;
      this._isUnlocked = false;
      this.deviceKey = null;
      console.log('Database closed');
    } catch (error) {
      console.error('Error closing database:', error);
      throw error;
    }
  }

  /**
   * Check if database is initialized
   */
  isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Check if device identity is unlocked
   */
  isUnlocked(): boolean {
    return this._isUnlocked;
  }

  // ============================================
  // MIGRATIONS
  // ============================================

  /**
   * Run database migrations
   */
  private async migrate(): Promise<void> {
    const version = await this.getSchemaVersion();

    if (version < CURRENT_SCHEMA_VERSION) {
      console.log(`Migrating database from version ${version} to ${CURRENT_SCHEMA_VERSION}`);

      if (version < 1) {
        await this.migrateToV1();
      }

      // Future migrations go here
      // if (version < 2) { await this.migrateToV2(); }
    }
  }

  /**
   * Initial schema migration
   */
  private async migrateToV1(): Promise<void> {
    // Create tables
    await this.db!.exec(SCHEMA_SQL);

    // Enable CRDT on business tables
    for (const table of CRDT_TABLES) {
      try {
        await this.db!.exec(`SELECT crsql_as_crr('${table}')`);
      } catch (error) {
        // Table might not exist yet or already registered
        console.warn(`Could not enable CRDT for ${table}:`, error);
      }
    }

    // Record migration
    await this.setSchemaVersion(1);
    console.log('Migrated to schema version 1');
  }

  /**
   * Get current schema version
   */
  private async getSchemaVersion(): Promise<number> {
    try {
      const result = await this.db!.execA<[number]>(
        'SELECT version FROM schema_version WHERE id = 1'
      );
      return result[0]?.[0] ?? 0;
    } catch {
      return 0;
    }
  }

  /**
   * Set schema version
   */
  private async setSchemaVersion(version: number): Promise<void> {
    await this.db!.exec(
      `INSERT OR REPLACE INTO schema_version (id, version, migrated_at) VALUES (1, ?, ?)`,
      [version, Date.now()]
    );
  }

  // ============================================
  // RAW SQL
  // ============================================

  /**
   * Execute SQL query and return results as objects
   * Uses execO to return rows with column names as keys
   */
  async exec<T = unknown>(sql: string, params?: unknown[]): Promise<T[]> {
    this.ensureInitialized();
    // Use execO to get objects with column names as keys (not execA which returns arrays)
    const result = await this.db!.execO(sql, params as (string | number | null | Uint8Array)[]);
    return result as T[];
  }

  /**
   * Execute SQL query and return results as arrays (raw format)
   * Use this when you need array-style results instead of objects
   */
  async execArray<T = unknown>(sql: string, params?: unknown[]): Promise<T[]> {
    this.ensureInitialized();
    const result = await this.db!.execA(sql, params as (string | number | null | Uint8Array)[]);
    return result as T[];
  }

  /**
   * Execute SQL statement (no return)
   */
  async run(sql: string, params?: unknown[]): Promise<void> {
    this.ensureInitialized();
    await this.db!.exec(sql, params as (string | number | null | Uint8Array)[]);
  }

  // ============================================
  // DEVICE IDENTITY
  // ============================================

  /**
   * Get device identity
   */
  async getIdentity(): Promise<DeviceIdentity | null> {
    this.ensureInitialized();

    const result = await this.db!.execO<{
      id: string;
      public_key: string;
      private_key_encrypted: string;
      name: string;
      created_at: number;
    }>('SELECT * FROM device_identity WHERE id = ?', ['device']);

    if (result.length === 0) return null;

    const row = result[0];
    return {
      id: 'device',
      publicKey: row.public_key,
      privateKeyEncrypted: row.private_key_encrypted,
      name: row.name,
      createdAt: row.created_at,
    };
  }

  /**
   * Create device identity
   */
  async createIdentity(name: string, passphrase: string): Promise<DeviceIdentity> {
    this.ensureInitialized();

    // Import crypto service dynamically to avoid circular deps
    const { cryptoService } = await import('./crypto');

    // Generate Ed25519 keypair
    const { publicKey, privateKey } = await cryptoService.generateKeyPair();

    // Derive encryption key from passphrase
    const salt = cryptoService.generateSalt();
    const deviceKey = await cryptoService.deriveKey(passphrase, salt);

    // Encrypt private key
    const { ciphertext, nonce } = await cryptoService.encrypt(privateKey, deviceKey);

    // Combine salt + nonce + ciphertext for storage
    const encryptedData = new Uint8Array([...salt, ...nonce, ...ciphertext]);
    const privateKeyEncrypted = btoa(String.fromCharCode(...encryptedData));

    const publicKeyBase64 = btoa(String.fromCharCode(...publicKey));
    const now = Date.now();

    await this.db!.exec(
      `INSERT INTO device_identity (id, public_key, private_key_encrypted, name, created_at)
       VALUES (?, ?, ?, ?, ?)`,
      ['device', publicKeyBase64, privateKeyEncrypted, name, now]
    );

    // Store device key in memory
    this.deviceKey = deviceKey;
    this._isUnlocked = true;

    return {
      id: 'device',
      publicKey: publicKeyBase64,
      privateKeyEncrypted,
      name,
      createdAt: now,
    };
  }

  /**
   * Unlock device identity with passphrase
   */
  async unlockIdentity(passphrase: string): Promise<boolean> {
    this.ensureInitialized();

    const identity = await this.getIdentity();
    if (!identity) {
      throw new Error('No device identity found');
    }

    try {
      const { cryptoService } = await import('./crypto');

      // Decode encrypted data
      const encryptedData = Uint8Array.from(atob(identity.privateKeyEncrypted), c => c.charCodeAt(0));

      // Extract salt (first 32 bytes), nonce (next 12 bytes), ciphertext (rest)
      const salt = encryptedData.slice(0, 32);
      const nonce = encryptedData.slice(32, 44);
      const ciphertext = encryptedData.slice(44);

      // Derive key and try to decrypt
      const deviceKey = await cryptoService.deriveKey(passphrase, salt);
      await cryptoService.decrypt(ciphertext, nonce, deviceKey);

      // If we get here, decryption succeeded
      this.deviceKey = deviceKey;
      this._isUnlocked = true;
      return true;
    } catch {
      return false;
    }
  }

  // ============================================
  // SPACES
  // ============================================

  /**
   * Get all spaces
   */
  async getSpaces(): Promise<Space[]> {
    this.ensureInitialized();

    const result = await this.db!.execO<{
      id: string;
      name: string;
      encrypted_key: string;
      salt: string;
      key_check: string;
      tables: string;
      created_at: number;
      joined_at: number;
    }>('SELECT * FROM spaces ORDER BY name');

    return result.map(row => ({
      id: row.id,
      name: row.name,
      encryptedKey: row.encrypted_key,
      salt: row.salt,
      keyCheck: row.key_check,
      tables: JSON.parse(row.tables),
      createdAt: row.created_at,
      joinedAt: row.joined_at,
    }));
  }

  /**
   * Get single space
   */
  async getSpace(id: string): Promise<Space | null> {
    this.ensureInitialized();

    const result = await this.db!.execO<{
      id: string;
      name: string;
      encrypted_key: string;
      salt: string;
      key_check: string;
      tables: string;
      created_at: number;
      joined_at: number;
    }>('SELECT * FROM spaces WHERE id = ?', [id]);

    if (result.length === 0) return null;

    const row = result[0];
    return {
      id: row.id,
      name: row.name,
      encryptedKey: row.encrypted_key,
      salt: row.salt,
      keyCheck: row.key_check,
      tables: JSON.parse(row.tables),
      createdAt: row.created_at,
      joinedAt: row.joined_at,
    };
  }

  /**
   * Create space
   */
  async createSpace(space: Space): Promise<void> {
    this.ensureInitialized();

    await this.db!.exec(
      `INSERT INTO spaces (id, name, encrypted_key, salt, key_check, tables, created_at, joined_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        space.id,
        space.name,
        space.encryptedKey,
        space.salt,
        space.keyCheck,
        JSON.stringify(space.tables),
        space.createdAt,
        space.joinedAt,
      ]
    );
  }

  /**
   * Delete space
   */
  async deleteSpace(id: string): Promise<void> {
    this.ensureInitialized();
    await this.db!.exec('DELETE FROM spaces WHERE id = ?', [id]);
  }

  /**
   * Get space members
   */
  async getSpaceMembers(spaceId: string): Promise<SpaceMember[]> {
    this.ensureInitialized();

    const result = await this.db!.execO<{
      space_id: string;
      public_key: string;
      name: string;
      role: string;
      added_at: number;
      added_by: string;
    }>('SELECT * FROM space_members WHERE space_id = ?', [spaceId]);

    return result.map(row => ({
      spaceId: row.space_id,
      publicKey: row.public_key,
      name: row.name,
      role: row.role as 'owner' | 'member',
      addedAt: row.added_at,
      addedBy: row.added_by,
    }));
  }

  /**
   * Add space member
   */
  async addSpaceMember(member: SpaceMember): Promise<void> {
    this.ensureInitialized();

    await this.db!.exec(
      `INSERT OR REPLACE INTO space_members (space_id, public_key, name, role, added_at, added_by)
       VALUES (?, ?, ?, ?, ?, ?)`,
      [member.spaceId, member.publicKey, member.name, member.role, member.addedAt, member.addedBy]
    );
  }

  /**
   * Remove space member
   */
  async removeSpaceMember(spaceId: string, publicKey: string): Promise<void> {
    this.ensureInitialized();
    await this.db!.exec(
      'DELETE FROM space_members WHERE space_id = ? AND public_key = ?',
      [spaceId, publicKey]
    );
  }

  // ============================================
  // CRDT SYNC
  // ============================================

  /**
   * Get changes since version
   */
  async getChanges(since: bigint): Promise<CRSQLChange[]> {
    this.ensureInitialized();

    const result = await this.db!.execA<
      [string, string, string, unknown, bigint, bigint, Uint8Array, number, number]
    >(
      `SELECT "table", pk, cid, val, col_version, db_version, site_id, cl, seq
       FROM crsql_changes
       WHERE db_version > ?
       ORDER BY db_version ASC`,
      [since]
    );

    return result.map(row => ({
      table: row[0],
      pk: JSON.parse(row[1]),
      cid: row[2],
      val: row[3],
      col_version: row[4],
      db_version: row[5],
      site_id: row[6],
      cl: row[7],
      seq: row[8],
    }));
  }

  /**
   * Apply remote changes
   */
  async applyChanges(changes: CRSQLChange[]): Promise<void> {
    this.ensureInitialized();

    for (const change of changes) {
      // Convert val to SQLite-compatible type
      const val = change.val as string | number | null | Uint8Array;

      await this.db!.exec(
        `INSERT INTO crsql_changes ("table", pk, cid, val, col_version, db_version, site_id, cl, seq)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          change.table,
          JSON.stringify(change.pk),
          change.cid,
          val,
          Number(change.col_version),
          Number(change.db_version),
          change.site_id,
          change.cl,
          change.seq,
        ]
      );
    }
  }

  /**
   * Get current database version
   */
  async getDbVersion(): Promise<bigint> {
    this.ensureInitialized();

    const result = await this.db!.execA('SELECT crsql_db_version()');
    const version = result[0]?.[0];
    return typeof version === 'bigint' ? version : BigInt(version ?? 0);
  }

  // ============================================
  // PEERS
  // ============================================

  /**
   * Get peers for a space
   */
  async getPeers(spaceId: string): Promise<SyncPeer[]> {
    this.ensureInitialized();

    const result = await this.db!.execO<{
      public_key: string;
      space_id: string;
      name: string;
      last_seen: number;
      db_version: number;
    }>('SELECT * FROM sync_peers WHERE space_id = ?', [spaceId]);

    return result.map(row => ({
      publicKey: row.public_key,
      spaceId: row.space_id,
      name: row.name,
      lastSeen: row.last_seen,
      dbVersion: BigInt(row.db_version),
    }));
  }

  /**
   * Update peer info
   */
  async updatePeer(peer: SyncPeer): Promise<void> {
    this.ensureInitialized();

    await this.db!.exec(
      `INSERT OR REPLACE INTO sync_peers (public_key, space_id, name, last_seen, db_version)
       VALUES (?, ?, ?, ?, ?)`,
      [peer.publicKey, peer.spaceId, peer.name, peer.lastSeen, Number(peer.dbVersion)]
    );
  }

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Ensure database is initialized
   */
  private ensureInitialized(): void {
    if (!this._isInitialized || !this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
  }

  /**
   * Get device encryption key (requires unlock)
   */
  getDeviceKey(): CryptoKey | null {
    return this.deviceKey;
  }
}

// Export singleton instance
export const databaseService = new DatabaseServiceImpl();
export { DatabaseServiceImpl };
